import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import {
  catchError,
  filter,
  ignoreElements,
  map,
  mergeMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { aiPlannerSettingsLoaded, setAIPlannerDisabled } from '@bridebook/web/lib/ai-planner/slice';
import { plannerAgent } from '@bridebook/web/lib/ai-planner/store/planner-agent';
import { appError } from '@bridebook/web/lib/app/actions';
import { IEpic } from '@bridebook/web/lib/types';

// Keeps the couple's on/off choice in sync with the wedding's planner agent
export const aiPlannerSettingsListenerEpic: IEpic = (action$, { state$ }) =>
  action$.pipe(
    ofType('INIT_WEDDING_LISTENER'),
    withLatestFrom(state$),
    filter(([, state]) => Boolean(state.weddings.profile.id)),
    mergeMap(([, state]) =>
      plannerAgent(state.weddings.profile.id)
        .observe()
        .pipe(
          // No agent yet means the planner was never turned off
          map((agent) => aiPlannerSettingsLoaded({ isDisabled: !!agent?.settings?.disabled })),
          takeUntil(action$.pipe(ofType('STOP_WEDDING_LISTENER'))),
        ),
    ),
  );

/**
 * Saves the couple's choice on the wedding's planner agent, so it holds for
 * everyone on the wedding and on every device. The agent is created on the
 * first save when the planner has not written it yet.
 */
export const saveAIPlannerSettingsEpic: IEpic = (action$, { state$ }) =>
  action$.pipe(
    filter(setAIPlannerDisabled.match),
    withLatestFrom(state$),
    filter(([, state]) => Boolean(state.weddings.profile.id)),
    mergeMap(([action, state]) => {
      const agent = plannerAgent(state.weddings.profile.id);
      const save = async () => {
        if (!(await agent.get(false)).exists()) {
          await agent.create({ settings: {} });
        }
        await agent.update({ 'settings.disabled': action.payload });
      };

      return from(save()).pipe(
        ignoreElements(),
        catchError((error: Error) => of(appError({ error, feature: 'AI planner settings' }))),
      );
    }),
  );
